import { VoiceAttachmentMeta } from "@/indexedDB/types";

import { Play, Pause, Loader, AlertCircle } from "lucide-react";

import { useVoiceAttachment } from "@/middleware/hooks/attachments/useVoiceAttachment";
import { useVoicePlayer } from "@/lib/voice";
import { Button } from "@/components/primitives/button";
import { AudioScrubber } from "@/components/primitives/waveform";
import { cn, secondsToMMSS } from "@/lib/utils";
import { useMemo } from "react";

type Props = {
  messageId: string;
  messageTimestamp: string;
  chatId: string;
  attachmentMeta: VoiceAttachmentMeta;
  isOutgoingMessage: boolean;
};

export default function VoiceAttachment({
  messageId,
  chatId,
  attachmentMeta,
  messageTimestamp,
  isOutgoingMessage,
}: Props) {
  const { getBlob } = useVoiceAttachment({
    messageId,
    messageTimestamp,
    chatId,
    attachment: attachmentMeta,
  });

  const {
    isPlaying,
    isPaused,
    loadState,
    currentTime,
    play,
    pause,
    resume,
    onSeekEnd,
  } = useVoicePlayer({
    chatId,
    messageId,
    getBlob,
  });

  const totalSeconds = Math.floor(attachmentMeta.duration / 1000);
  const formattedTotalSeconds = secondsToMMSS(totalSeconds);

  const isUnsupported = loadState === "unsupported";

  const isLoading = loadState === "fetching" || loadState === "buffering";

  const outgoingBarColor = useMemo(() => {
    if (!isOutgoingMessage) {
      return undefined;
    }
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue("--primary-foreground")
      .trim();
    return raw ? `hsl(${raw})` : undefined;
  }, [isOutgoingMessage]);

  const handlePlayPause = () => {
    if (isUnsupported || isLoading) {
      return; // Can't play — format not supported or still loading
    }
    if (loadState === "error") {
      play(); // retry
    } else if (isPlaying) {
      pause();
    } else if (isPaused) {
      resume();
    } else {
      play();
    }
  };

  return (
    <div
      className={cn(
        "flex min-w-44 flex-1 items-center gap-3 px-1 align-middle",
        isOutgoingMessage
          ? "bg-primary text-primary-foreground"
          : "bg-accent text-accent-foreground",
      )}
    >
      {isLoading ? (
        <Button
          type="button"
          size="icon"
          className="size-11 shrink-0 rounded-full border-2 bg-transparent hover:bg-transparent"
        >
          <Loader className="size-5 animate-spin" />
        </Button>
      ) : (
        <Button
          type="button"
          size="icon"
          onClick={handlePlayPause}
          disabled={isUnsupported}
          className={cn(
            "size-11 shrink-0 rounded-full border bg-transparent hover:bg-transparent",
            isOutgoingMessage
              ? "border-primary-foreground text-primary-foreground"
              : "border-foreground text-foreground",
            isUnsupported && "cursor-not-allowed opacity-50",
          )}
        >
          {isUnsupported && <AlertCircle className="size-5" />}
          {!isUnsupported && isPlaying && <Pause className="size-5" />}
          {!isUnsupported && !isPlaying && <Play className="size-5" />}
        </Button>
      )}

      {isUnsupported ? (
        <div className="flex-1 text-xs opacity-60">
          Audio format not supported by this browser
        </div>
      ) : (
        <AudioScrubber
          className="w-full min-w-40"
          data={attachmentMeta.waveform}
          currentTime={currentTime}
          duration={totalSeconds}
          showHandle={false}
          barColor={outgoingBarColor}
          barWidth={2}
          height={60}
          onSeek={onSeekEnd}
        />
      )}

      <div className="text-xs opacity-50">{formattedTotalSeconds}</div>
    </div>
  );
}
